import { useState } from 'react'
import { apiClient, ApiError } from '../lib/api'
import { useChatStore } from '../stores/chatStore'
import { useToastStore } from '../stores/toastStore'
import { track } from '../lib/analytics'
import ConfirmDialog from './ConfirmDialog'

const CONTENT_ITEM_WARNING_THRESHOLD = 4

function getContentItemCount(): number {
  const entries = useChatStore.getState().entries
  return entries.filter((e) => e.content_item != null).length
}

type URLType = 'youtube' | 'blog'

interface Props {
  url: string
  urlType: URLType
  threadId: number
  onDismiss: () => void
  onImportSuccess?: () => void
}

const YOUTUBE_RE =
  /https?:\/\/(?:www\.|m\.)?youtube\.com\/(?:watch|shorts\/|embed\/|live\/)|https?:\/\/youtu\.be\//i

function extractURL(text: string): string | null {
  const trimmed = text.trim()
  // Pure URL — entire text is the link
  if (/^https?:\/\/\S+$/i.test(trimmed)) return trimmed
  // URL embedded in share text (e.g. "Video Title\nhttps://youtu.be/abc")
  const match = trimmed.match(/https?:\/\/\S+/i)
  return match ? match[0] : null
}

export function detectPastedURL(text: string): { url: string; urlType: URLType } | null {
  const url = extractURL(text)
  if (!url) return null
  // YouTube patterns (including m.youtube.com and /live/)
  if (YOUTUBE_RE.test(url)) {
    return { url, urlType: 'youtube' }
  }
  // Any other URL is treated as a blog/article
  try {
    new URL(url)
    return { url, urlType: 'blog' }
  } catch {
    return null
  }
}

export default function URLDetectionCard({ url, urlType, threadId, onDismiss, onImportSuccess }: Props) {
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDuplicate, setIsDuplicate] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingForce, setPendingForce] = useState(false)

  const isYouTube = urlType === 'youtube'
  const label = isYouTube ? 'YouTube video detected' : 'Web article detected'
  const importLabel = isYouTube ? 'Import Transcript' : 'Import Article'

  const doImport = async (force = false) => {
    setImporting(true)
    setError(null)
    setIsDuplicate(false)
    const contentType = isYouTube ? 'youtube' : 'blog'
    track('content_import_started', { thread_id: threadId, content_type: contentType })
    try {
      const endpoint = isYouTube
        ? `research/threads/${threadId}/ingest/youtube/`
        : `research/threads/${threadId}/ingest/blog/`
      await apiClient.post(endpoint, { url, force })
      track('content_import_completed', { thread_id: threadId, content_type: contentType })
      useToastStore.getState().addToast(
        isYouTube ? 'Importing transcript...' : 'Importing article...',
        'success'
      )
      onImportSuccess?.()
      onDismiss()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { detail?: string }
        setError(body?.detail ?? 'This content is already in this thread.')
        setIsDuplicate(true)
      } else {
        track('content_import_failed', { thread_id: threadId, content_type: contentType, error: 'Import failed' })
        setError('Import failed. Try again.')
      }
    } finally {
      setImporting(false)
    }
  }

  const handleImport = (force = false) => {
    if (getContentItemCount() >= CONTENT_ITEM_WARNING_THRESHOLD) {
      setPendingForce(force)
      setConfirmOpen(true)
    } else {
      doImport(force)
    }
  }

  return (
    <div className="mx-4 mb-2 p-3 bg-elevated rounded-xl border border-accent/20 animate-in fade-in">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isYouTube ? (
            <svg className="w-5 h-5 text-red-500 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          ) : (
            <svg className="w-5 h-5 text-accent shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-4.247m0 0A8.966 8.966 0 013 12c0-1.264.26-2.466.727-3.559" />
            </svg>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary">{label}</p>
            <p className="text-xs text-text-muted truncate">{url}</p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="p-1 text-text-muted hover:text-text-secondary transition-colors shrink-0"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {error && <p className="text-xs text-error mt-1.5">{error}</p>}

      <div className="mt-2.5">
        {isDuplicate ? (
          <button
            onClick={() => handleImport(true)}
            disabled={importing}
            className="w-full py-2 px-3 bg-accent text-bg text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {importing ? 'Importing...' : 'Import Anyway'}
          </button>
        ) : (
          <button
            onClick={() => handleImport()}
            disabled={importing}
            className="w-full py-2 px-3 bg-accent text-bg text-sm font-medium rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            {importing ? 'Importing...' : importLabel}
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Lots of imports"
        message="This thread already has several imported items. Adding more may shorten the AI's memory of your conversation. Consider starting a new thread for new topics."
        confirmLabel="Import Anyway"
        onConfirm={() => {
          setConfirmOpen(false)
          doImport(pendingForce)
        }}
        onCancel={() => {
          setConfirmOpen(false)
          setPendingForce(false)
        }}
      />
    </div>
  )
}
